/**
 * BarraSuperior — 64 px, fixa no alto da coluna de conteúdo (padrão A, item 6).
 *
 * Da esquerda para a direita, SEMPRE nesta ordem nos cinco sistemas:
 *   [botão da gaveta, só < 1024 px] · trilha "Grupo › Tela" · (espaço) · "Ir para…" · avisos (sino) ·
 *   tema (Claro/Escuro/Sistema) · pessoa (nome, e-mail e PAPEL em cinza) · "Sair".
 * Abaixo de 640 px tema, pessoa, papel e Sair somem daqui e viram o bloco "Conta" DENTRO da gaveta.
 *
 * ⚠️ Por que existe (NAV-08, medido em 11/10/2026): no Sentinela "Sair" e o tema ficavam no fim da rolagem
 * do menu — 115 e 183 px ABAIXO da dobra em 1366×768 — e usuário e papel não apareciam em lugar nenhum.
 * Num painel de plantão compartilhado, quem está logado e como sair têm de estar sempre à vista.
 *
 * A trilha é TEXTO (`<ol>` dentro de `<nav aria-label="Trilha">`), nunca um `<h1>`: o `<h1>` é um só e fica
 * na página. Não leva `aria-current`: o único `aria-current="page"` do documento é o item do menu.
 */
import { ChevronRight, LogOut, Menu, Search } from "lucide-react";
import type { ReactNode, RefObject } from "react";
import { SeletorDeTema } from "./SeletorDeTema.js";
import type { PreferenciaTema, UsuarioShell } from "./tipos.js";

export interface BarraSuperiorProps {
  trilha: { grupo?: string; tela: string } | null;
  /** Mostra o botão da gaveta (viewport < 1024 px). */
  comBotaoMenu: boolean;
  menuAberto: boolean;
  idDaGaveta: string;
  aoAbrirMenu: () => void;
  botaoMenuRef: RefObject<HTMLButtonElement>;
  /** "Ir para…" só existe com mais de 20 telas visíveis. */
  comBusca: boolean;
  aoAbrirBusca: () => void;
  /** Sino/avisos, se o sistema tiver. */
  avisos?: ReactNode;
  preferenciaTema: PreferenciaTema;
  aoMudarTema: (p: PreferenciaTema) => void;
  usuario: UsuarioShell | null;
  aoSair: () => void;
}

export function BarraSuperior(p: BarraSuperiorProps) {
  return (
    <header className="casca-topo">
      {p.comBotaoMenu && (
        <button
          type="button"
          ref={p.botaoMenuRef}
          className="casca-btn"
          aria-label="Abrir menu"
          aria-expanded={p.menuAberto}
          aria-haspopup="dialog"
          aria-controls={p.idDaGaveta}
          onClick={p.aoAbrirMenu}
        >
          <Menu size={22} aria-hidden="true" />
        </button>
      )}

      {p.trilha && (
        <nav aria-label="Trilha de navegação" className="casca-trilha">
          <ol>
            {p.trilha.grupo && (
              <li className="casca-trilha-grupo">
                <span>{p.trilha.grupo}</span>
                <ChevronRight size={14} aria-hidden="true" />
              </li>
            )}
            <li className="casca-trilha-tela"><span>{p.trilha.tela}</span></li>
          </ol>
        </nav>
      )}

      <div className="casca-topo-fim">
        {p.comBusca && (
          <button type="button" className="casca-btn casca-busca" aria-haspopup="dialog" aria-keyshortcuts="/ Control+K" onClick={p.aoAbrirBusca}>
            <Search size={18} aria-hidden="true" />
            <span className="casca-busca-texto">Ir para…</span>
            <kbd className="casca-tecla" aria-hidden="true">/</kbd>
          </button>
        )}

        {p.avisos}

        <SeletorDeTema preferencia={p.preferenciaTema} aoMudar={p.aoMudarTema} className="casca-so-sm" />

        {p.usuario && (
          <div className="casca-pessoa casca-so-sm">
            <div className="casca-pessoa-nome casca-so-lg">
              <span className="casca-pessoa-linha1">{p.usuario.nome}</span>
              {p.usuario.email && <span className="casca-pessoa-linha2">{p.usuario.email}</span>}
            </div>
            <span className="casca-papel">{p.usuario.papelRotulo}</span>
          </div>
        )}

        <button type="button" className="casca-btn casca-sair casca-so-sm" onClick={p.aoSair}>
          <LogOut size={18} aria-hidden="true" />
          <span>Sair</span>
        </button>
      </div>
    </header>
  );
}
